/**
 * Контракты подбора: прогон, результаты, персональная карточка (BE-080…BE-088).
 *
 * Зеркало `backend/app/modules/search/schemas.py`. Генерации типов из OpenAPI в
 * проекте пока нет, поэтому контракт описан руками и обязан совпадать с
 * backend — расхождение ловит E2E на реальном стеке.
 *
 * Три правила чтения, которые нельзя «упростить»:
 *
 * * **объяснение — код, а не текст.** `code + params + evidence` переводит
 *   фронт через i18n; вывода без evidence не существует (CLAUDE.md §3 п. 6);
 * * **«не считали» и «посчитали, ничего не нашли» — разные ответы.**
 *   `market = null` означает, что рыночная оценка для автомобиля не
 *   рассчитывалась; посчитанное отсутствие сравнения приходит статусом
 *   `insufficient_data`. `attention_count = 0` **не означает** «замечаний нет»;
 * * **неприменимый компонент — не ноль и не 50.** У него `raw_score = null`,
 *   он исключён из модели, и его вес перераспределён (MATCHING §8.1).
 */

import type {
  BodyType,
  BenchmarkMatchLevel,
  Drivetrain,
  FuelType,
  MarketEvaluationStatus,
  Transmission,
  VatStatus,
  VehicleCoverImage,
  VehicleLocation,
  VehiclePrice,
  VehicleSeller,
} from '@entities/vehicle/types';
import type { ScoreComponent } from '@entities/search-session/types';

export type { ScoreComponent };

/** Статусы прогона (`search_runs.status`). `stale` — каталог изменился. */
export type SearchRunStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'stale';

/**
 * Стадии экрана ожидания (MVP_PRD §12). Worker пишет стадию на границах фаз,
 * поэтому прогресс реальный, а не имитация таймером.
 */
export type SearchRunStage = 'analyzing' | 'matching' | 'scoring' | 'explaining';

export const SEARCH_RUN_STAGES: readonly SearchRunStage[] = [
  'analyzing',
  'matching',
  'scoring',
  'explaining',
];

export interface SearchRunProgress {
  stage: SearchRunStage | null;
  /** 0–100; `null` у `failed` — прогресса больше не будет. */
  percent: number | null;
}

export interface SearchRunState {
  id: string;
  search_session_id: string;
  criteria_version_id: string;
  status: SearchRunStatus;
  progress: SearchRunProgress;
  algorithm_version: string;
  catalog_revision: number | null;
  total_catalog_count: number | null;
  /** Сколько автомобилей прошло обязательные критерии. `0` — состояние AC-09. */
  eligible_count: number | null;
  /** Сколько сохранено: персист усечён до 500 на прогон (MATCHING §10). */
  result_count: number | null;
  /** Итоговые веса компонентов после акцентов — шапка результатов (FE-081). */
  configured_weights: Record<string, number> | null;
  /**
   * Согласованная ступень географического набора: `home` | `neighbours` | `country`.
   * `null` — домашняя земля неизвестна либо ступенчатый набор не применялся.
   */
  geo_expansion_step: string | null;
  /**
   * Сколько автомобилей даёт каждая ступень (MVP_PRD §43.3), ключи — коды ступеней.
   *
   * `null` — считать нечего, и строка географии на экране анализа **не
   * рисуется**: «в вашей земле 0» было бы выдуманным фактом. Числа считает
   * прогон по всем eligible, а не по согласованным ступеням (BE-395).
   */
  geo_step_counts: Record<string, number> | null;
  error_code: string | null;
  is_stale: boolean;
  /** Прогон-пересчёт той же версии критериев после смены ревизии каталога. */
  refresh_run_id: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface SearchRunAccepted {
  search_run_id: string;
  status: SearchRunStatus;
}

// --- Объяснения (BE-085) -----------------------------------------------------------------

/** Основание вывода. Без хотя бы одного основания вывод не создаётся. */
export type EvidenceItem =
  | { type: 'criterion'; path: string; value: unknown }
  | { type: 'vehicle_field'; field: string; value: unknown }
  | {
      type: 'benchmark';
      benchmark_id: string | null;
      median_price_cents: number | null;
      sample_size: number | null;
      snapshot_date: string | null;
    }
  | { type: 'calculation'; code: string; params: Record<string, unknown> };

/** Уровень значимости компромисса (MVP_PRD §14). */
export type CompromiseSeverity = 'low' | 'medium' | 'high';

export interface ExplanationItem {
  code: string;
  params: Record<string, unknown>;
  /** Порядок вывода уже задан backend; число нужно только для стабильной сортировки. */
  importance: number;
  evidence: EvidenceItem[];
  /** Есть только у компромиссов. */
  severity?: CompromiseSeverity;
}

// --- Соответствие и рынок ----------------------------------------------------------------

export type ConfidenceCategory = 'high' | 'medium' | 'low';
export type MatchCategoryCode = 'very_high' | 'good' | 'partial' | 'weak';

export interface MatchInfo {
  /** 0–100. Не оценка техсостояния и не вероятность удачной покупки (MVP_PRD §2). */
  score: number;
  category: MatchCategoryCode;
  match_confidence: number;
  match_confidence_category: ConfidenceCategory | '';
  data_confidence: number;
  /** Доля применённой модели, 0–100 (MATCHING §8.3). */
  scoring_coverage: number;
  /** Первые причины соответствия для карточки: две (MVP_PRD §19). */
  top_reasons: ExplanationItem[];
}

/**
 * Рыночный блок карточки результата — **снимок на момент прогона**.
 *
 * Числовых полей нет при `unavailable` (снимок старше 90 дней) и
 * `insufficient_data`: правило «числа не показываются» соблюдено данными.
 */
export interface MarketSnapshot {
  status: MarketEvaluationStatus;
  match_level: BenchmarkMatchLevel | null;
  median_price_cents: number | null;
  delta_cents: number | null;
  delta_percent: number | null;
  sample_size: number | null;
  snapshot_date: string | null;
  is_stale: boolean;
}

/**
 * Закрытая карточка (BE-206, MVP_PRD §34.2).
 *
 * С BE-580 витрина своего рабочего набора до оплаты приходит уровнем `open`;
 * закрытой остаётся карточка автомобиля **вне** рабочего набора своего заказа
 * (в полном списке оплаченного заказа — результаты ниже порога или за
 * пределами первых `SHORTLIST_MAX`). Ни ранга, ни Match Score, ни цены, ни
 * отметки избранного: полей под закрытые значения здесь нет — они не приходят
 * с сервера, и нарисовать их нечем.
 */
export interface SearchResultTeaserData {
  access: 'teaser';
  vehicle_id: string;
  slug: string;
  make: string;
  model: string;
  first_registration_year: number;
  mileage_km: number;
  cover_image: VehicleCoverImage | null;
}

/**
 * Данные объявления в карточке результата — общие для `open` и `full`
 * (`SearchResultOpenOut` и `SearchResultCardOut`, BE-580, BE-581).
 *
 * Аналитики здесь нет: её добавляет только `SearchResultCardData`.
 */
interface SearchResultListing {
  vehicle_id: string;
  slug: string;
  /** Статус объявления на момент чтения (`published`, …). */
  status: string;
  title: string;
  make: string;
  model: string;
  variant: string | null;
  body_type: BodyType;
  price: VehiclePrice;
  vat_status: VatStatus;
  negotiable: boolean | null;
  first_registration_date: string;
  mileage_km: number;
  fuel_type: FuelType;
  transmission: Transmission;
  /** `null` — источник не указал привод (домысливать его нельзя). */
  drivetrain: Drivetrain | null;
  power_kw: number | null;
  location: VehicleLocation;
  cover_image: VehicleCoverImage | null;
  is_new: boolean;
  /** Автохаус: имя, тип, город, земля. E-mail не приходит ни на одном уровне. */
  seller: VehicleSeller | null;
  /** Объявление на сайте автохауса — только `http`/`https`; `null` — ссылки нет. */
  listing_url: string | null;
  data_observed_at: string;
  /** Автомобиль отмечен в этом заказе (BE-581); до и после оплаты одинаково. */
  is_favorite: boolean;
}

/**
 * Открытая карточка витрины до оплаты (`SearchResultOpenOut`, BE-580, FE-586).
 *
 * Данные объявления своего рабочего набора бесплатны, аналитика — нет: ни
 * ранга, ни Match Score, ни рынка, ни признаков внимания в ответе **нет
 * физически**, поэтому нет и полей под них. Порядок витрины по умолчанию
 * случайный и **не является рейтингом** — ранг восстановил бы ранжирование, то
 * есть ровно ту работу, за которую ещё не заплачено.
 */
export interface SearchResultOpenData extends SearchResultListing {
  access: 'open';
}

/** Полная карточка оплаченного заказа: объявление и аналитика подбора. */
export interface SearchResultCardData extends SearchResultListing {
  access: 'full';
  rank: number;
  match: MatchInfo;
  /** `null` — рыночная оценка для автомобиля не рассчитывалась. */
  market: MarketSnapshot | null;
  attention_count: number;
}

// --- Пустой результат (AC-09) ------------------------------------------------------------

/** Сколько автомобилей отсеяло обязательное условие. `rule` — код hard-правила. */
export interface LimitingCriterion {
  rule: string;
  excluded: number;
}

/**
 * Предложение по смягчению критериев.
 *
 * **Никогда не применяется автоматически:** сервер критерии не меняет, а фронт
 * не имеет права «поправить» запрос за пользователя (MATCHING §5.3).
 */
export interface RelaxationSuggestionData {
  code: string;
  rule: string;
  params: Record<string, unknown>;
  /** Сколько автомобилей добавилось бы при этом изменении. */
  would_add: number;
}

/**
 * Требование, смягчённое низким покрытием каталога (KARL_DIALOG_RFC §6.4).
 *
 * Не «мы поправили ваш запрос»: документ критериев не менялся. Правило
 * hard-фильтра не отличает «нет опции» от «нет данных», и обязательное
 * требование к полю, которого в объявлениях почти нет, отсеивало бы всё.
 */
export interface DemotedRequirement {
  code: 'FEATURE_DEMOTED_LOW_COVERAGE' | 'THRESHOLD_DEMOTED_LOW_COVERAGE';
  path: string;
  /** Код признака оснащения или имя поля порога. */
  value: string;
  coverage: number;
}

/** Почему список пуст: ни один не прошёл критерии или не прошёл доп. фильтры. */
export type EmptyReason =
  | 'NO_ELIGIBLE_VEHICLES'
  | 'NO_FILTER_MATCHES'
  // Подходящие автомобили есть, но не в согласованной географии: ответ на
  // это — расширить поиск, а не сбрасывать фильтры (MVP_PRD §43.3).
  | 'NO_REGION_MATCHES'
  // Критерии прошли, но Match Score у всех ниже порога рабочего набора
  // (MVP_PRD §34.1): сбрасывать нечего, помогает только уточнение запроса.
  | 'ONLY_WEAK_MATCHES'
  // Включено «Meine Favoriten», а отмеченных автомобилей в текущем наборе нет
  // (BE-581). Набор не пуст — выход один: показать все автомобили.
  | 'NO_FAVORITES';

/** Карточка результата в одном из трёх режимов; различаются по `access`. */
export type SearchResultItem = SearchResultCardData | SearchResultOpenData | SearchResultTeaserData;

export interface SearchResultsPageData {
  /**
   * `true` — витрина рабочего набора до оплаты (BE-207, BE-580): карточки
   * `open`, порядок по умолчанию детерминированно случайный и **не является
   * рейтингом**. `false` — полный список оплаченного заказа.
   */
  showcase: boolean;
  items: SearchResultItem[];
  page: number;
  page_size: number;
  /** Сколько автомобилей в выдаче при этих параметрах (с `favorites_only` — отмеченных). */
  total: number;
  total_pages: number;
  /**
   * Отмеченные автомобили рабочего набора этого прогона (BE-581) — число для
   * «Meine Favoriten (n)». Фильтры и `favorites_only` его не меняют.
   */
  favorites_count: number;
  is_stale: boolean;
  refresh_run_id: string | null;
  empty_reason: EmptyReason | null;
  limiting_criteria: LimitingCriterion[];
  relaxation_suggestions: RelaxationSuggestionData[];
  /**
   * Требования, действовавшие как желательные из-за пустого поля в каталоге
   * (KARL_DIALOG_RFC §6.4). Приходят **всегда**, а не только при пустой выдаче.
   */
  demoted: DemotedRequirement[];
}

// --- Персональная карточка (FE-083) ------------------------------------------------------

export interface ScoreComponentBreakdown {
  component_code: ScoreComponent;
  /** `false` — компонент исключён из модели для этого автомобиля. */
  applicable: boolean;
  /** `null` ровно при `applicable = false`. Ни 0, ни 50, ни 100 не подставляется. */
  raw_score: number | null;
  configured_weight: number;
  /** Доля в нормализованной сумме, проценты. */
  effective_weight: number;
  weighted_score: number;
  evidence: EvidenceItem[];
  calculation_version: string;
}

/** Фактор, повысивший или понизивший уверенность (модалка «Как рассчитано»). */
export interface ConfidenceFactor {
  code: string;
  direction: 'up' | 'down';
  params: Record<string, unknown>;
}

export interface DataConfidenceFactors {
  score: number;
  category: ConfidenceCategory;
  completeness: number;
  freshness: number;
  provenance: number;
  consistency: number;
  /** Коды противоречий в данных объявления; пустой список — противоречий нет. */
  conflicts: string[];
  factors: ConfidenceFactor[];
}

export interface MatchConfidenceFactors {
  score: number;
  category: ConfidenceCategory;
  criteria_richness: number;
  scoring_coverage: number;
  inapplicable_components: ScoreComponent[];
  factors: ConfidenceFactor[];
}

export interface ConfidenceFactors {
  data: DataConfidenceFactors;
  match: MatchConfidenceFactors;
}

/** Снимок признаков внимания на момент прогона: счётчик и до пяти кодов. */
export interface AttentionSummary {
  count: number;
  flags: { code: string; severity: string }[];
}

/**
 * «Auto 3 von 100» и стрелки ‹ › карточки автомобиля в заказе (BE-580).
 *
 * Считается по тем же `sort` и фильтрам, что список, — одной функцией порядка.
 * `position` — с единицы; `null` — автомобиля нет в списке при этих параметрах
 * (фильтр его отсекает), тогда нет и соседей.
 */
export interface ResultNavigation {
  position: number | null;
  total: number;
  previous_vehicle_id: string | null;
  next_vehicle_id: string | null;
}

export interface SearchResultDetailData {
  access: 'full';
  search_run_id: string;
  vehicle_id: string;
  slug: string;
  rank: number;
  match: MatchInfo;
  /** Пустой объект у прогонов, сохранённых без разбора уверенности. */
  confidence_factors: ConfidenceFactors | Record<string, never>;
  components: ScoreComponentBreakdown[];
  inapplicable_components: ScoreComponent[];
  why_matches: ExplanationItem[];
  advantages: ExplanationItem[];
  /** Факты без знака: ни довод за автомобиль, ни компромисс (MATCHING §9). */
  neutral_facts: ExplanationItem[];
  compromises: ExplanationItem[];
  attention_summary: AttentionSummary | Record<string, never>;
  market: MarketSnapshot | null;
  /** Автомобиль отмечен в этом заказе (BE-581). */
  is_favorite: boolean;
  navigation: ResultNavigation;
  is_stale: boolean;
  refresh_run_id: string | null;
  created_at: string;
}

/**
 * Карточка автомобиля в заказе до оплаты (`SearchResultOpenDetailOut`, BE-580)
 * — вместо прежнего `403`.
 *
 * Разбора соответствия здесь **нет вовсе**: ни ранга, ни Match Score, ни
 * причин, ни рынка — это аналитика, за которую платят. Данные объявления экран
 * берёт из `GET /vehicles/{slug}` (уровень `open`), отсюда — только адрес
 * объявления, отметка избранного и навигация по списку.
 */
export interface SearchResultOpenDetailData {
  access: 'open';
  vehicle_id: string;
  slug: string;
  is_favorite: boolean;
  navigation: ResultNavigation;
}

/** Ответ `GET /search-runs/{id}/results/{vehicle_id}` в одном из двух режимов. */
export type SearchResultDetailItem = SearchResultDetailData | SearchResultOpenDetailData;

/**
 * Сортировки витрины до оплаты (BE-580) — только по открытым значениям.
 *
 * Порядка по соответствию и по уверенности здесь нет: ранжирование — работа, за
 * которую ещё не заплачено, и backend отвечает на них `400`. Без `sort` витрина
 * идёт в детерминированно случайном порядке. Порядок перечисления — порядок
 * пунктов в списке «Sortieren» (FE-586).
 */
export const SHOWCASE_SORTS = [
  'price_asc',
  'price_desc',
  'mileage_asc',
  'registration_desc',
  'newest',
] as const;

export type ShowcaseSort = (typeof SHOWCASE_SORTS)[number];

/** Откуда пользователь ушёл на объявление автохауса (BE-582, `ListingOpenedIn.via`). */
export type ListingOpenedVia = 'self_service' | 'dealer_block';

/** Закрытая ли карточка результата. Одна проверка вместо `'price' in item`. */
export function isResultTeaser(item: SearchResultItem): item is SearchResultTeaserData {
  return item.access === 'teaser';
}

/** Открытая ли карточка — витрина своего набора до оплаты (BE-580). */
export function isResultOpen(item: SearchResultItem): item is SearchResultOpenData {
  return item.access === 'open';
}

/** Полная ли карточка — оплаченный заказ, с аналитикой подбора. */
export function isResultFull(item: SearchResultItem): item is SearchResultCardData {
  return item.access === 'full';
}
